import { UserIcon, Play } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import defaultProjectPlaceHolder from '../../../assets/images/project_placeholder@2x.jpg';
import whiteBgSbProfile from '../../../assets/images/white_bg_sb_profile.jpeg';
import Image from '../../Image';
import SkeletonLoader from '../../shared/SkeletonLoader';

interface FilePreview {
  file_url: string;
  file_name: string;
  default_file_url?: any;
  file_content_type: string;
  showControl?: boolean;
  dynamicHeight?: boolean;
  object_fit?: string;
  reCalculateHeight?: boolean;
  poster_url?: string;
  isThumbnail?: boolean;
}

const FilePreviewer: React.FC<FilePreview> = ({
  file_content_type,
  file_url,
  file_name,
  default_file_url,
  showControl,
  dynamicHeight,
  object_fit,
  reCalculateHeight,
  poster_url,
  isThumbnail
}) => {
  const divRef = useRef(null);
  const [heightImage, setHeightImage] = useState('100%');
  const [fileUrlSelected, setfileUrlSelected] = useState('')
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  // Function to calculate and set the height
  useEffect(() => {
    setfileUrlSelected(file_url);
    if (file_content_type?.startsWith('video')) {
      setVideoLoaded(false);
    } else if (file_content_type?.startsWith('image')) {
      setImageLoaded(false);
    }
  }, [file_url, file_content_type]);
  
  const calculateHeight = () => {
    if (divRef.current) {
      // Get the current width of the div
      const width = divRef.current.offsetWidth;
      // Set the height based on a 4:5 ratio (height = width * 5/4)
      if (file_content_type.startsWith('image') && !isThumbnail) {
        // console.log('FilePreviewer: width', width, 'height', width * (5 / 4) + 'px');
        setHeightImage(width * (5 / 4) + 'px');
      } else if (file_content_type.startsWith('video') || isThumbnail) {
        // console.log('videowidth', width, 'height', width * (9 / 16) + 'px');
        setHeightImage(width * (9 / 16) + 'px');
      }
    } else {
      // If ref is not ready, we can check again very briefly
      setTimeout(() => {
        calculateHeight();
      }, 100);
    }
  };

    useEffect(() => {
      if(reCalculateHeight != undefined && reCalculateHeight == true){
        console.log("ref:reCalculateHeight", reCalculateHeight);
        calculateHeight();
      }
  }, [reCalculateHeight]);

  useEffect(() => {
    if (dynamicHeight == undefined || dynamicHeight) {
      calculateHeight();
      window.addEventListener('resize', calculateHeight);
    }
    return () => {
      if (dynamicHeight == undefined || dynamicHeight) {
        window.removeEventListener('resize', calculateHeight);
      }
    };
  }, []);

  return ( //console.log('file_url', file_url, 'fileUrlSelected', fileUrlSelected),
    <div
      className="flex flex-none justify-center items-center w-full h-full relative bg-dark-deep-gray"
      ref={divRef}
      style={{
        height: dynamicHeight === false ? '100%' : `${heightImage}`,
        aspectRatio: (file_content_type.startsWith('video') || isThumbnail) ? '16/9' : '4/5',
        // backgroundImage: `url(${whiteBgSbProfile})`,
        // backgroundSize: 'cover',
        // backgroundPosition: 'center',
        // backgroundColor: '#FFFFFF'
      }}
    >
      {file_content_type.startsWith('image') ? (
        <div className="relative w-full h-full">
          {!imageLoaded && !isThumbnail && (
            <div className="absolute inset-0 w-full h-full z-10">
              <SkeletonLoader />
            </div>
          )}
          <Image
            className={"w-full h-full " + (isThumbnail ? "" : "transition-opacity duration-300 ") + (object_fit != undefined ? object_fit : " object-cover ") + (imageLoaded || isThumbnail ? ' opacity-100' : ' opacity-0')}
            src={fileUrlSelected == 'icons/default_avatar.svg' ? defaultProjectPlaceHolder : fileUrlSelected}
            draggable="false"
            fallbackSrc={default_file_url || defaultProjectPlaceHolder}
            onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
              setImageLoaded(true);
              const img = e.currentTarget;
              const isLandscape = img.naturalWidth > img.naturalHeight;

              // For landscape images, use object-cover (fill container, no black bars)
              // For portrait/square images, use object-contain (full image with letterboxing)
              if (object_fit === undefined) {
                // Remove both classes first
                img.className = img.className.replace(/ object-cover/g, '').replace(/ object-contain/g, '');
                // Add the appropriate class
                img.className += isLandscape ? ' object-cover' : ' object-contain';
              }
            }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              setImageLoaded(true);

            }}          />
        </div>
      ) : file_content_type.startsWith('video') ? (
        <div className="relative flex w-full h-full ">
          {!videoLoaded && !poster_url && (
            <div className="absolute inset-0 w-full h-full z-10">
              <SkeletonLoader />
            </div>
          )}
          <div className="relative flex w-full h-full justify-center">
            <video
              src={file_url}
              controls={showControl ?? true}
              className={object_fit != undefined ? object_fit : "object-contain"}
              poster={poster_url}
              onLoadedMetadata={() => setVideoLoaded(true)}
              onLoadedData={() => setVideoLoaded(true)}
              onCanPlay={() => setVideoLoaded(true)}
            // style={{ height: `${heightImage}` }}
            >
              Your browser does not support the video tag.
            </video>
          </div>
          {!showControl && (
            <div className="absolute bottom-1 flex pointer-events-none">
              <Play className="text-white bg-white-500 object-contain" />
            </div>
          )}
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 mx-auto text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0014.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
          <p className="mt-2 text-sm">{file_name}</p>
          <p className="text-xs text-gray-400">({file_content_type})</p>
        </div>
      )}
    </div>
  );
};

export default FilePreviewer;
